import { ConnectedPosition, CdkOverlayOrigin, CdkConnectedOverlay } from '@angular/cdk/overlay';
import {
  Component,
  ViewEncapsulation,
  forwardRef,
  ChangeDetectorRef,
  OnDestroy,
  ElementRef,
  inject,
  input,
  output,
  effect,
  signal,
  viewChild,
  computed
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { FormsModule } from '@angular/forms';
import { Observable, Subscription, Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { ButtonComponent } from '../button/button.component';
import { InputComponent } from '../input/input.component';

/**
 * Custom select component with support for both synchronous and asynchronous options.
 *
 * Features:
 * - ngModel support via ControlValueAccessor
 * - Async options support via Observable<any[]>
 * - Typeahead/search functionality with debouncing
 * - Loading state management
 * - Custom binding for value and label properties
 * - Keyboard navigation support
 *
 * Usage examples:
 *
 * Synchronous options:
 * <app-select
 *   [(ngModel)]="selectedValue"
 *   [options]="staticOptions"
 *   bindValue="id"
 *   bindLabel="name">
 * </app-select>
 *
 * Asynchronous options:
 * <app-select
 *   [(ngModel)]="selectedValue"
 *   [options]="options$"
 *   bindValue="id"
 *   bindLabel="name">
 * </app-select>
 *
 * Or using asyncOptions input:
 * <app-select
 *   [(ngModel)]="selectedValue"
 *   [asyncOptions]="options$"
 *   bindValue="id"
 *   bindLabel="name">
 * </app-select>
 *
 * With typeahead/search functionality:
 * <app-select
 *   [(ngModel)]="selectedValue"
 *   [options]="options"
 *   [searchable]="true"
 *   [searchPlaceholder]="'Search options...'"
 *   [minSearchLength]="2"
 *   [debounceTime]="300"
 *   bindValue="id"
 *   bindLabel="name"
 *   (search)="onSearch($event)">
 * </app-select>
 */
@Component({
    selector: 'app-select',
    templateUrl: './select.component.html',
    styleUrls: ['./select.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectComponent),
            multi: true
        }
    ],
    imports: [CdkOverlayOrigin, CdkConnectedOverlay, FormsModule, ButtonComponent, InputComponent]
})
export class SelectComponent implements OnDestroy, ControlValueAccessor {
  private cdr = inject(ChangeDetectorRef);

  // Signal inputs
  options = input<unknown[] | Observable<unknown[]> | null>([]);
  asyncOptions = input<Observable<unknown[]> | null>(null);
  disabled = input(false);
  multi = input(false);
  bindValue = input<string | undefined>(undefined);
  bindLabel = input<string | undefined>(undefined);
  loading = input(false);
  placeholder = input('Select an option');
  searchable = input(false);
  searchPlaceholder = input('Search...');
  minSearchLength = input(0);
  debounceTimeMs = input(300, { alias: 'debounceTime' });
  clearable = input(false);

  // Signal outputs
  change = output<unknown>();
  search = output<string>();

  // Signal view children
  originElement = viewChild<ElementRef>('trigger');
  searchInput = viewChild<InputComponent>('searchInput');

  /** Internal disabled state that can be toggled by CVA setDisabledState */
  _disabledByCva = signal(false);

  /** Effective disabled state */
  isDisabled = computed(() => this.disabled() || this._disabledByCva());

  /** Internal loading state that can be toggled by async subscriptions */
  _loadingInternal = signal(false);

  /** Effective loading state */
  isLoading = computed(() => this.loading() || this._loadingInternal());

  placeholderVisible = false;

  private _selected: unknown = null;
  private _isOpen: boolean = false;
  private currentValue: unknown = null;
  private optionsSubscription: Subscription | null = null;
  private _options: unknown[] = [];
  private _filteredOptions: unknown[] = [];
  private searchSubject = new Subject<string>();
  private searchSubscription: Subscription | null = null;
  searchTerm = '';
  highlightedIndex = -1;
  searchValue = '';

  constructor() {
    // Set up search subscription with debouncing
    this.searchSubscription = this.searchSubject
      .pipe(debounceTime(this.debounceTimeMs()), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.performSearch(searchTerm);
      });
  }

  /** React to options input changes */
  private optionsEffect = effect(() => {
    const opts = this.options();
    this.handleOptionsChangeInternal(opts);
  });

  /** React to asyncOptions input changes */
  private asyncOptionsEffect = effect(() => {
    const asyncOpts = this.asyncOptions();
    if (asyncOpts) {
      this.subscribeToOptions(asyncOpts);
    }
  });

  /** React to disabled input changes - close overlay when disabled */
  private disabledEffect = effect(() => {
    if (this.isDisabled() && this._isOpen) {
      this._isOpen = false;
      this.cdr.detectChanges();
    }
  });

  get originWidth(): number {
    const el = this.originElement();
    return el?.nativeElement?.offsetWidth || 0;
  }

  get selected(): unknown {
    return this._selected;
  }

  get currentOptions(): unknown[] {
    return this.searchable() ? this._filteredOptions : this._options;
  }

  get isSearching(): boolean {
    return this.searchable() && this.searchTerm.length >= this.minSearchLength();
  }

  set selected(value: unknown) {
    this._selected = value;
    this.cdr.detectChanges();
  }

  get isOpen(): boolean {
    return this._isOpen;
  }

  set isOpen(value: boolean) {
    this._isOpen = value;
    this.cdr.detectChanges();
  }

  get selectedLabel(): string {
    const bLabel = this.bindLabel();
    if (this.multi()) {
      if (!this.selected || (this.selected as unknown[]).length === 0) {
        this.placeholderVisible = true;
        return this.placeholder();
      }
      this.placeholderVisible = false;
      if ((this.selected as unknown[]).length === 1) {
        const first = (this.selected as Record<string, unknown>[])[0];
        return bLabel ? String(first[bLabel]) : String(first);
      }
      return `${(this.selected as unknown[]).length} items selected`;
    }

    const selected = this.selected;
    if (!selected) {
      this.placeholderVisible = true;
      return this.placeholder();
    }
    this.placeholderVisible = false;
    return bLabel ? String((selected as Record<string, unknown>)[bLabel]) : String(selected);
  }

  // ControlValueAccessor implementation
  private onChange = (_value: unknown) => {};
  private onTouched = () => {};

  writeValue(value: unknown): void {
    this.currentValue = value;
    const bValue = this.bindValue();

    if (value == null) {
      this.selected = this.multi() ? [] : null;
      return;
    }

    if (bValue) {
      if (this.multi()) {
        if (Array.isArray(value)) {
          this.selected = this._options.filter((opt: unknown) => value.includes((opt as Record<string, unknown>)[bValue]));
        } else {
          this.selected = [];
        }
      } else {
        const option = this._options.find((opt: unknown) => (opt as Record<string, unknown>)[bValue] === value);
        this.selected = option || null;
      }
    } else {
      this.selected = value;
    }
  }

  registerOnChange(fn: (_value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabledByCva.set(isDisabled);
  }

  isSelected(option: unknown): boolean {
    if (this.multi()) {
      return !!(this.selected && (this.selected as unknown[]).some((item: unknown) => item === option));
    }
    const selected = this.selected;
    if (!selected) return false;
    if (this.bindValue()) {
      return selected === option;
    }
    return option === selected;
  }

  overlayPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top'
    },
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top'
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom'
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom'
    }
  ];

  toggleDropdown(): void {
    // don't open/close if disabled
    if (this.isDisabled()) return;
    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      this.onDropdownOpen();
    } else {
      this.clearSearch();
    }
  }

  selectOption(option: unknown): void {
    if (this.isDisabled()) return;
    const bValue = this.bindValue();

    if (this.multi()) {
      const selectedItems = (this.selected as unknown[]) || [];
      const index = selectedItems.findIndex((item: unknown) => item === option);
      if (index > -1) {
        selectedItems.splice(index, 1);
      } else {
        selectedItems.push(option);
      }
      this.selected = [...selectedItems];
      const values = (this.selected as unknown[]).map((item: unknown) => (bValue ? (item as Record<string, unknown>)[bValue] : item));
      this.onChange(values);
      this.change.emit(values);
    } else {
      const selectedOption = bValue ? option : option;
      const value = bValue ? (option as Record<string, unknown>)[bValue] : option;

      this.selected = selectedOption;
      this.onChange(value);
      this.onTouched();
      this.change.emit(value);
      this.isOpen = false;
    }

    if (this.searchable()) {
      this.clearSearch();
    }
  }

  clearSelection(event: Event): void {
    event.stopPropagation();
    if (this.multi()) {
      this.selected = [];
      this.onChange([]);
      this.change.emit([]);
    } else if (this.clearable()) {
      this.selected = null;
      this.onChange(null);
      this.change.emit(null);
    }
  }

  ngOnDestroy(): void {
    if (this.optionsSubscription) {
      this.optionsSubscription.unsubscribe();
    }
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  private handleOptionsChangeInternal(opts: unknown[] | Observable<unknown[]> | null): void {
    if (Array.isArray(opts)) {
      this._options = opts;
      this._filteredOptions = [...this._options];
      this.updateSelectedFromCurrentValue();
    } else if (opts instanceof Observable) {
      this.subscribeToOptions(opts);
    }
  }

  private subscribeToOptions(observable: Observable<unknown[]>): void {
    // Unsubscribe from previous subscription
    if (this.optionsSubscription) {
      this.optionsSubscription.unsubscribe();
    }

    this._loadingInternal.set(true);
    this.optionsSubscription = observable.subscribe({
      next: (options) => {
        this._options = options;
        this._filteredOptions = [...this._options];
        this._loadingInternal.set(false);
        this.updateSelectedFromCurrentValue();
        this.cdr.detectChanges();
      },
      error: () => {
        this._loadingInternal.set(false);
        this._options = [];
        this._filteredOptions = [];
        this.cdr.detectChanges();
      }
    });
  }

  private updateSelectedFromCurrentValue(): void {
    const bValue = this.bindValue();
    if (this.currentValue != null && bValue && this._options.length > 0) {
      if (this.multi()) {
        if (Array.isArray(this.currentValue)) {
          this.selected = this._options.filter((opt) => (this.currentValue as unknown[]).includes((opt as Record<string, unknown>)[bValue]));
        }
      } else {
        const option = this._options.find((opt: unknown) => (opt as Record<string, unknown>)[bValue] === this.currentValue);
        this.selected = option || null;
      }
    }
  }

  // Search methods
  onSearchValueChange(): void {
    this.searchTerm = this.searchValue;
    this.highlightedIndex = -1;

    if (this.searchTerm.length >= this.minSearchLength()) {
      this.searchSubject.next(this.searchTerm);
    } else {
      this._filteredOptions = [...this._options];
      this.cdr.detectChanges();
    }
  }

  onSearchKeyDown(event: KeyboardEvent): void {
    if (!this.searchable()) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightNext();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightPrevious();
        break;
      case 'Enter':
        event.preventDefault();
        this.selectHighlighted();
        break;
      case 'Escape':
        event.preventDefault();
        this.isOpen = false;
        break;
    }
  }

  private performSearch(searchTerm: string): void {
    if (!this.searchable()) return;
    const bLabel = this.bindLabel();

    this.search.emit(searchTerm);

    if (searchTerm.length < this.minSearchLength()) {
      this._filteredOptions = [...this._options];
    } else {
      this._filteredOptions = this._options.filter((option) => {
        const searchText = bLabel ? String((option as Record<string, unknown>)[bLabel]) : String(option);
        return searchText.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    this.highlightedIndex = -1;
    this.cdr.detectChanges();
  }

  private highlightNext(): void {
    if (this._filteredOptions.length === 0) return;
    this.highlightedIndex = (this.highlightedIndex + 1) % this._filteredOptions.length;
    this.cdr.detectChanges();
  }

  private highlightPrevious(): void {
    if (this._filteredOptions.length === 0) return;
    this.highlightedIndex = this.highlightedIndex <= 0 ? this._filteredOptions.length - 1 : this.highlightedIndex - 1;
    this.cdr.detectChanges();
  }

  private selectHighlighted(): void {
    if (this.highlightedIndex >= 0 && this.highlightedIndex < this._filteredOptions.length) {
      this.selectOption(this._filteredOptions[this.highlightedIndex]);
    }
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.searchValue = '';
    this.highlightedIndex = -1;
    this._filteredOptions = [...this._options];
    this.cdr.detectChanges();
  }

  isHighlighted(option: unknown): boolean {
    if (!this.searchable()) return false;
    const index = this._filteredOptions.findIndex((opt) => opt === option);
    return index === this.highlightedIndex;
  }

  /** Whether the clear button should be shown */
  get showClearButton(): boolean {
    if (this.isDisabled()) return false;
    if (this.multi()) {
      return !!this.selected && (this.selected as unknown[]).length > 0;
    }
    return this.clearable() && !!this.selected;
  }

  onDropdownOpen(): void {
    if (this.searchable()) {
      const si = this.searchInput();
      if (si) {
        // Focus search input when dropdown opens
        setTimeout(() => {
          const inputElement = (si as unknown as { elementRef?: { nativeElement?: HTMLElement } }).elementRef?.nativeElement?.querySelector('input');
          if (inputElement) {
            inputElement.focus();
          }
        }, 0);
      }
    }
  }
}
